// ─────────────────────────────────────────────────────────────
// PREACH READY™ — all copy, links and image URLs live here.
// To use real images, paste a full URL into any empty string below.
// Empty strings render a marked placeholder slot.
// ─────────────────────────────────────────────────────────────

export const CHECKOUT_URL = 'https://preach-ready.impultienda.ar/checkout';
export const META_PIXEL_ID = '1089071093911713';

export const PAGE_TITLE = 'Preach Ready™ | Your Whole Preaching Year, Planned';
export const META_DESCRIPTION =
  'A 52-week preaching plan plus 10 complete ready-to-preach sermons. Open it, read it, preach it.';

// ── IMAGE SLOTS ──────────────────────────────────────────────
const RES = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/70b30586-ddd8-4cdb-83e1-68610a4c2b1a/';
const COVER = (id: string) =>
  `https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/render/image/public/ebook-images/70b30586-ddd8-4cdb-83e1-68610a4c2b1a/${id}-mockup.webp?width=720&quality=85&resize=contain`;

/** Box mockup: main product + 5 bonus booklets (transparent background) */
export const heroMockupUrl = RES + '1792523190362_mockup-caja-sin-fondo-67672973.webp';
/** iMac + tablets + phones mockup (transparent background) */
export const offerMockupUrl = RES + '1792523196428_mockup-imac-sin-fondo-e3c63b2e.webp';
/** 6 items, in the same order as pageCaptions */
export const pageImages: string[] = [
  RES + '1792510930284_pagina-01-cover-19b14ff5.webp',
  RES + '1792510935043_pagina-03-system-ac2f0683.webp',
  RES + '1792510944830_pagina-08-sermon-cover-c1046ef0.webp',
  RES + '1792510949610_pagina-09-sermon-map-2e8a7bbd.webp',
  RES + '1792510954366_pagina-10-preach-it-3b4261ce.webp',
  RES + '1792510939916_pagina-04-kit-c3b807ef.webp',
];
/** 5 items, in the same order as bonuses */
export const bonusCovers: string[] = [
  COVER('0373b7fd-74a5-4074-ae20-c68a9b164615'),
  COVER('ef8782db-c7fb-4c2e-8b19-246a2b74ea83'),
  COVER('8b10ecff-ad87-4206-8510-a15631a11830'),
  COVER('83ace5e2-03a9-4c13-92e6-808054f4b383'),
  COVER('cf825043-5785-4f60-b8b2-78d3893f2ed6'),
];

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'PREACH READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'GET MY YEAR PLANNED';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY YEAR PLANNED · $27';
export const price = '$27';

// ── 1. TOP BAR ───────────────────────────────────────────────
export const topBar = { lookInside: 'Look inside' };

// ── 2. HERO ──────────────────────────────────────────────────
export const hero = {
  eyebrow: 'FOR PASTORS WHO PREACH EVERY WEEK',
  title: 'YOUR WHOLE PREACHING YEAR, PLANNED IN ONE PACK.',
  sub: '52 weeks mapped and 10 complete sermons ready to preach. Open it, read it, preach it on Sunday.',
  stats: [
    { value: '52', label: 'WEEKS PLANNED' },
    { value: '10', label: 'COMPLETE SERMONS' },
    { value: '30', label: 'MINUTE FLOW' },
    { value: '5', label: 'BONUSES INCLUDED' },
  ],
};

// ── 3. SOUND FAMILIAR ────────────────────────────────────────
export const pains = {
  eyebrow: 'SOUND FAMILIAR?',
  title: 'YOU WERE CALLED TO PREACH. NOT TO STARE AT A BLANK PAGE.',
  items: [
    { title: 'SATURDAY NIGHT, BLANK PAGE', text: "A full week of ministry, and Sunday's message still isn't written." },
    { title: 'WHAT DO I PREACH NEXT?', text: 'Without a plan, every week starts from zero.' },
    {
      title: 'HOW DO I MAKE IT PRACTICAL?',
      text: 'Explaining the text is one thing. Showing your people what to do on Monday is another.',
    },
    { title: 'HOW DO I CLOSE?', text: 'The invitation deserves more than the last five minutes of preparation.' },
  ],
  closing: "IT'S NOT A LACK OF CALLING. IT'S A LACK OF TIME.",
};

// ── 4. SOLUTION ──────────────────────────────────────────────
export const solution = {
  eyebrow: 'MEET PREACH READY™',
  title: 'THE PLANNING IS DONE. YOU JUST OPEN IT AND PREACH.',
  text: 'A 52-week preaching plan maps every Sunday of your year. Volume 01, Foundations of Faith, gives you the first 10 sermons fully prepared — Bible-based, non-denominational, and ready to adapt to your own voice.',
  steps: [
    { title: 'OPEN IT', text: "Pick this week's sermon from your 52-week plan." },
    { title: 'READ IT', text: 'The text, the big idea, the outline and the illustrations are already there.' },
    { title: 'PREACH IT', text: 'Follow the minute-by-minute flow and close with a clear invitation.' },
  ],
};

// ── 5. FEATURES ──────────────────────────────────────────────
export const features = {
  title: 'EVERY SERMON, FULLY PREPARED.',
  sub: 'All 10 sermons follow the same clear 3-page structure, so you always know what to read, say, show and ask.',
  items: [
    { icon: 'idea', title: 'THE BIG IDEA', text: 'One sentence your people can repeat.' },
    { icon: 'outline', title: '3-POINT OUTLINE FROM THE TEXT', text: 'Every point comes from the passage.' },
    { icon: 'hook', title: 'OPENING HOOK & ILLUSTRATIONS', text: 'Start strong and make it visible.' },
    { icon: 'timer', title: 'MINUTE-BY-MINUTE FLOW', text: 'A 30-minute timing bar, with 20 and 45-minute options.' },
    { icon: 'prayer', title: 'INVITATION & CLOSING PRAYER', text: 'End with a clear next step, never pressure.' },
    { icon: 'group', title: 'SMALL GROUP QUESTIONS', text: 'Take Sunday into the week.' },
  ],
} as const;

// ── 6. SERMONS ───────────────────────────────────────────────
export const sermons = {
  title: 'VOLUME 01 · FOUNDATIONS OF FAITH',
  sub: 'Preach them as a 10-week series or pick the one your church needs this Sunday.',
  items: [
    { n: '01', title: 'Saved by Grace', ref: 'Ephesians 2:1–10' },
    { n: '02', title: 'Faith That Pleases God', ref: 'Hebrews 11:1–6' },
    { n: '03', title: 'Lord, Teach Us to Pray', ref: 'Matthew 6:5–13' },
    { n: '04', title: 'Built on the Rock', ref: 'Matthew 7:24–27' },
    { n: '05', title: 'The Power of Forgiveness', ref: 'Matthew 18:21–35' },
    { n: '06', title: 'Hope in the Storm', ref: 'Mark 4:35–41' },
    { n: '07', title: 'Who God Says You Are', ref: '2 Corinthians 5:14–21' },
    { n: '08', title: 'Called to Serve', ref: 'John 13:1–17' },
    { n: '09', title: 'A Generous Heart', ref: '2 Corinthians 9:6–15' },
    { n: '10', title: 'Sent on Mission', ref: 'Matthew 28:16–20' },
  ],
};

// ── 7. LOOK INSIDE ───────────────────────────────────────────
export const lookInside = {
  eyebrow: 'LOOK INSIDE',
  title: 'SEE THE PAGES BEFORE YOU BUY.',
  hint: 'Swipe to browse · Tap any page to view full size',
  captions: [
    'Cover',
    'The Preach Ready system',
    'Sermon at a glance',
    'The sermon map',
    'Preach it: minute by minute',
    'Your preparation kit',
  ],
};

// ── 8. BONUSES ───────────────────────────────────────────────
export const bonuses = {
  eyebrow: 'INCLUDED AT NO EXTRA COST',
  title: 'PLUS 5 BONUSES FOR THE WHOLE YEAR.',
  items: [
    {
      label: 'BONUS 01',
      title: 'THE 52-WEEK PREACHING PLAN',
      text: 'Your whole preaching year mapped week by week: series, theme, main text and big idea for every Sunday, with Easter and Christmas already placed.',
    },
    {
      label: 'BONUS 02',
      title: 'PLAN B READY — 10 LOW-PREP SERMONS FOR HARD WEEKS',
      text: 'For the weeks when a funeral, a crisis or a full-time job leaves no time: complete outlines you can preach with 30 minutes of preparation.',
    },
    {
      label: 'BONUS 03',
      title: 'SPECIAL SUNDAYS SERMON PACK',
      text: "Easter, Good Friday, Christmas, Mother's Day, Father's Day, Thanksgiving, New Year and Baptism Sunday, ready before they arrive.",
    },
    {
      label: 'BONUS 04',
      title: 'OPENING HOOK LIBRARY',
      text: '52 ways to start your sermon: questions, everyday scenes and object lessons, each connected to the big idea in under 60 seconds.',
    },
    {
      label: 'BONUS 05',
      title: 'ALTAR CALL & INVITATION SCRIPTS',
      text: 'Ready-to-read invitations for salvation, recommitment, prayer and serving, plus a simple follow-up plan.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────
export const offer = {
  title: 'YOUR PREACHING YEAR, READY TO PREACH.',
  cardTitle: 'PREACH READY™ · VOLUME 01 + 52-WEEK PLAN + ALL 5 BONUSES',
  includedTitle: "WHAT'S INCLUDED",
  included: [
    '52-week preaching plan',
    '10 complete ready-to-preach sermons',
    'Big idea & 3-point outline for each',
    'Illustrations & application',
    'Minute-by-minute 30-min flow',
    'Invitation, closing prayer & small group questions',
  ],
  bonusesTitle: 'THE 5 BONUSES',
  priceNote: 'USD · ONE-TIME PAYMENT · NO SUBSCRIPTION',
  /** The ONLY button that goes to checkout */
  cta: 'GET PREACH READY™ NOW — $27',
  microcopy: '10-day money-back guarantee · No questions asked',
  delivery: ['Digital PDF', 'Read on phone, tablet or print', 'Available worldwide'],
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "If it isn't a fit for your ministry, request a refund within 10 days of purchase through your order contact channel and we'll give your money back.",
};

// ── 11. FAQ ──────────────────────────────────────────────────
export const faq = {
  eyebrow: 'QUESTIONS',
  title: 'BEFORE YOU DECIDE.',
  items: [
    {
      q: 'What exactly do I get?',
      a: 'A 52-week preaching plan, Volume 01 "Foundations of Faith" with 10 complete ready-to-preach sermons (big idea, 3-point outline, illustrations, application, minute-by-minute flow, invitation, closing prayer and small group questions) and the 5 bonuses. All as instant PDF downloads.',
    },
    {
      q: 'Does this really plan my whole year?',
      a: 'Yes. The 52-Week Preaching Plan maps every Sunday of your year with the series, theme, main text and big idea. Volume 01 gives you the first 10 sermons fully prepared, and the Special Sundays pack and Plan B sermons cover the dates and weeks that usually catch pastors unprepared.',
    },
    {
      q: 'Is it tied to a specific denomination?',
      a: "No. The sermons stay within historic, widely shared Christian teaching and avoid divisive topics. Adapt them to your church's statement of faith.",
    },
    {
      q: 'Which Bible translation does it use?',
      a: 'Quotations are from the King James Version. You can preach with any translation you prefer.',
    },
    { q: 'Can I change the sermons?', a: 'Yes. They are built to be adapted to your voice, your stories and your congregation.' },
    { q: 'How long is each sermon?', a: 'About 30 minutes, with simple 20 and 45-minute options.' },
    {
      q: 'Is this a printed book?',
      a: "No. It's a digital PDF you can read on your phone or tablet at the pulpit, or print.",
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CHOICE ─────────────────────────────────────────
export const finalChoice = {
  title: 'NEXT SUNDAY CAN START WITH A PLAN.',
  without: {
    title: 'KEEP STARTING FROM ZERO',
    items: [
      'Another Saturday night with a blank page',
      'Searching for an outline that fits',
      'Illustrations pieced together at the last minute',
      'Hoping the close lands',
    ],
  },
  with: {
    title: 'WITH PREACH READY™',
    items: [
      'Every Sunday of the year planned',
      'The big idea and outline ready',
      'Illustrations and application done',
      'A clear invitation and next step',
    ],
  },
  microcopy: '10 complete sermons + 52-week plan + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ───────────────────────────────────────────────
export const footer = {
  line: 'PREACH READY™ · Digital resource for pastors and church leaders',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
